/**
 * LUXWORLD Blackout Curtains Collection V2
 * Asset: lux-blackout-collection-v2.js
 * Version: 2.0.0
 *
 * Responsibilities:
 * - Daylight / Curtains Closed / Enhanced Blackout tabs
 * - Room recommendation tabs
 * - FAQ accordion behaviour
 * - Mobile sticky CTA behaviour
 * - CTA analytics events
 * - Accessibility keyboard controls
 * - Shopify Theme Editor reinitialisation
 */

(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-lux-blackout-collection-v2]";
  const INITIALISED_ATTRIBUTE = "data-lux-bc-initialised";
  const MOBILE_BREAKPOINT = 680;

  function trackEvent(root, eventName, details = {}) {
    if (!eventName) return;

    const payload = {
      event: eventName,
      page_type: "blackout-curtains-collection-v2",
      collection_handle:
        root.getAttribute("data-collection-handle") ||
        "blackout-curtains-perth-custom-made-residential-commercial",
      ...details
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);

    try {
      root.dispatchEvent(
        new CustomEvent("luxworld:blackout-collection-event", {
          bubbles: true,
          detail: payload
        })
      );
    } catch (error) {
      console.warn("[LUXWORLD Blackout Collection V2] Event dispatch failed.", error);
    }
  }

  function activateTabGroup({
    tabs,
    panels,
    selectedTab,
    getTabKey,
    getPanelKey,
    focusTab = false
  }) {
    const selectedKey = getTabKey(selectedTab);
    if (!selectedKey) return;

    tabs.forEach((tab) => {
      const active = tab === selectedTab;
      tab.classList.toggle("is-active", active);
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.setAttribute("tabindex", active ? "0" : "-1");
    });

    panels.forEach((panel) => {
      const active = getPanelKey(panel) === selectedKey;
      panel.classList.toggle("is-active", active);
      panel.hidden = !active;
      panel.setAttribute("aria-hidden", active ? "false" : "true");
      panel.setAttribute("tabindex", active ? "0" : "-1");
    });

    if (focusTab) {
      selectedTab.focus({ preventScroll: true });
    }
  }

  function bindKeyboardNavigation(tabList, tabs, activate) {
    tabList.addEventListener("keydown", (event) => {
      const currentIndex = tabs.indexOf(document.activeElement);
      if (currentIndex < 0) return;

      let nextIndex = currentIndex;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      activate(tabs[nextIndex], true);
    });
  }

  function initialiseSceneTabs(root) {
    root.querySelectorAll("[data-lux-bc-scene]").forEach((scene) => {
      if (!(scene instanceof HTMLElement) || scene.dataset.luxBcSceneReady === "true") {
        return;
      }

      const tabList = scene.querySelector('[role="tablist"]');
      const tabs = Array.from(
        scene.querySelectorAll("[data-lux-bc-scene-mode]")
      ).filter((item) => item instanceof HTMLElement);

      const panels = Array.from(
        scene.querySelectorAll("[data-lux-bc-scene-panel]")
      ).filter((item) => item instanceof HTMLElement);

      if (!(tabList instanceof HTMLElement) || !tabs.length || !panels.length) {
        return;
      }

      const activate = (tab, focusTab = false) => {
        activateTabGroup({
          tabs,
          panels,
          selectedTab: tab,
          getTabKey: (item) => item.dataset.luxBcSceneMode,
          getPanelKey: (item) => item.dataset.luxBcScenePanel,
          focusTab
        });

        trackEvent(root, "blackout_scene_changed", {
          scene_mode: tab.dataset.luxBcSceneMode || ""
        });
      };

      tabs.forEach((tab) => {
        tab.addEventListener("click", () => activate(tab));
      });

      bindKeyboardNavigation(tabList, tabs, activate);

      const activeTab =
        tabs.find((tab) => tab.getAttribute("aria-selected") === "true") ||
        tabs[0];

      activateTabGroup({
        tabs,
        panels,
        selectedTab: activeTab,
        getTabKey: (item) => item.dataset.luxBcSceneMode,
        getPanelKey: (item) => item.dataset.luxBcScenePanel
      });

      scene.dataset.luxBcSceneReady = "true";
    });
  }

  function initialiseRoomTabs(root) {
    root.querySelectorAll("[data-lux-bc-room-explorer]").forEach((explorer) => {
      if (
        !(explorer instanceof HTMLElement) ||
        explorer.dataset.luxBcRoomReady === "true"
      ) {
        return;
      }

      const tabList = explorer.querySelector('[role="tablist"]');
      const tabs = Array.from(
        explorer.querySelectorAll("[data-lux-bc-room-tab]")
      ).filter((item) => item instanceof HTMLElement);

      const panels = Array.from(
        explorer.querySelectorAll("[data-lux-bc-room-panel]")
      ).filter((item) => item instanceof HTMLElement);

      if (!(tabList instanceof HTMLElement) || !tabs.length || !panels.length) {
        return;
      }

      const activate = (tab, focusTab = false) => {
        activateTabGroup({
          tabs,
          panels,
          selectedTab: tab,
          getTabKey: (item) => item.dataset.luxBcRoomTab,
          getPanelKey: (item) => item.dataset.luxBcRoomPanel,
          focusTab
        });

        if (window.innerWidth <= MOBILE_BREAKPOINT) {
          tab.scrollIntoView({
            behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
              ? "auto"
              : "smooth",
            block: "nearest",
            inline: "center"
          });
        }

        trackEvent(root, "blackout_room_changed", {
          room_key: tab.dataset.luxBcRoomTab || "",
          room_name: (tab.textContent || "").trim()
        });
      };

      tabs.forEach((tab) => {
        tab.addEventListener("click", () => activate(tab));
      });

      bindKeyboardNavigation(tabList, tabs, activate);

      const activeTab =
        tabs.find((tab) => tab.getAttribute("aria-selected") === "true") ||
        tabs[0];

      activateTabGroup({
        tabs,
        panels,
        selectedTab: activeTab,
        getTabKey: (item) => item.dataset.luxBcRoomTab,
        getPanelKey: (item) => item.dataset.luxBcRoomPanel
      });

      explorer.dataset.luxBcRoomReady = "true";
    });
  }

  function initialiseFaq(root) {
    root.querySelectorAll(".lux-bc-v2__faq-list").forEach((list) => {
      if (!(list instanceof HTMLElement) || list.dataset.luxBcFaqReady === "true") {
        return;
      }

      const detailsItems = Array.from(list.querySelectorAll("details"));

      detailsItems.forEach((details) => {
        details.addEventListener("toggle", () => {
          if (!details.open) return;

          detailsItems.forEach((other) => {
            if (other !== details) other.open = false;
          });

          const summary = details.querySelector("summary");
          trackEvent(root, "blackout_faq_opened", {
            question: (summary?.textContent || "").trim()
          });
        });
      });

      list.dataset.luxBcFaqReady = "true";
    });
  }

  function initialiseCtaTracking(root) {
    root
      .querySelectorAll("[data-lux-bc-event], [data-lux-bc-builder-link]")
      .forEach((element) => {
        if (
          !(element instanceof HTMLElement) ||
          element.dataset.luxBcTrackingReady === "true"
        ) {
          return;
        }

        element.addEventListener("click", () => {
          const eventName =
            element.dataset.luxBcEvent ||
            (element.hasAttribute("data-lux-bc-builder-link")
              ? "blackout_builder_enter"
              : "blackout_cta_click");

          trackEvent(root, eventName, {
            label: (element.textContent || "").trim(),
            destination:
              element instanceof HTMLAnchorElement ? element.href : ""
          });
        });

        element.dataset.luxBcTrackingReady = "true";
      });
  }

  function initialiseMobileCta(root) {
    const mobileCta = root.querySelector("[data-lux-bc-mobile-cta]");
    if (!(mobileCta instanceof HTMLElement)) return;
    if (mobileCta.dataset.luxBcMobileReady === "true") return;

    const hero = root.querySelector(".lux-bc-v2__hero");
    const finalCta = root.querySelector(".lux-bc-v2__final-cta");
    let heroVisible = true;
    let finalVisible = false;

    const update = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      const shouldHide = !mobile || heroVisible || finalVisible;

      mobileCta.classList.toggle("is-hidden", shouldHide);
      mobileCta.setAttribute("aria-hidden", shouldHide ? "true" : "false");
    };

    if ("IntersectionObserver" in window) {
      if (hero instanceof HTMLElement) {
        const heroObserver = new IntersectionObserver(
          (entries) => {
            heroVisible = entries.some((entry) => entry.isIntersecting);
            update();
          },
          { threshold: 0.05 }
        );
        heroObserver.observe(hero);
      } else {
        heroVisible = false;
      }

      if (finalCta instanceof HTMLElement) {
        const finalObserver = new IntersectionObserver(
          (entries) => {
            finalVisible = entries.some((entry) => entry.isIntersecting);
            update();
          },
          { threshold: 0.12 }
        );
        finalObserver.observe(finalCta);
      }
    } else {
      heroVisible = false;
    }

    window.addEventListener("resize", update, { passive: true });
    update();

    mobileCta.dataset.luxBcMobileReady = "true";
  }

  function announce(root, message) {
    const region = root.querySelector("[data-lux-bc-announcement]");
    if (!(region instanceof HTMLElement) || !message) return;

    region.textContent = "";
    window.setTimeout(() => {
      region.textContent = message;
    }, 40);

    window.setTimeout(() => {
      if (region.textContent === message) {
        region.textContent = "";
      }
    }, 3500);
  }

  function initialiseRoot(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.getAttribute(INITIALISED_ATTRIBUTE) === "true") return;

    initialiseSceneTabs(root);
    initialiseRoomTabs(root);
    initialiseFaq(root);
    initialiseCtaTracking(root);
    initialiseMobileCta(root);

    root.addEventListener("luxworld:blackout-collection-announce", (event) => {
      const customEvent = event;
      announce(root, String(customEvent.detail?.message || ""));
    });

    root.setAttribute(INITIALISED_ATTRIBUTE, "true");

    trackEvent(root, "blackout_collection_loaded");
  }

  function initialiseAll(context = document) {
    const roots = [];

    if (context instanceof HTMLElement && context.matches(ROOT_SELECTOR)) {
      roots.push(context);
    }

    context.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      if (root instanceof HTMLElement) roots.push(root);
    });

    roots.forEach(initialiseRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initialiseAll(document),
      { once: true }
    );
  } else {
    initialiseAll(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    const target = event.target;
    if (target instanceof HTMLElement) initialiseAll(target);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const block = event.target;
    if (!(block instanceof HTMLElement)) return;

    const root = block.closest(ROOT_SELECTOR);
    if (!(root instanceof HTMLElement)) return;

    const roomPanel = block.closest("[data-lux-bc-room-panel]");
    if (roomPanel instanceof HTMLElement) {
      const key = roomPanel.dataset.luxBcRoomPanel || "";
      const tab = root.querySelector(
        `[data-lux-bc-room-tab="${CSS.escape(key)}"]`
      );
      if (tab instanceof HTMLElement) tab.click();
    }

    block.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
      block: "center"
    });
  });
})();
